import { makeRedirectUri } from "expo-auth-session";
import * as PKCE from "expo-auth-session/build/PKCE";
import { useCallback } from "react";
import {
  saveOAuthState,
  withNextInState,
  type OAuthProvider,
} from "./oauthState";

const NONCE_LENGTH = 32;

type WebOAuthLoginOptions = {
  endpoint: string;
  clientId: string;
  redirectPath: string;
  scope: string;
  next?: string;
  beforeSignIn?: () => Promise<boolean>;
};

/**
 * The press handler of a web login button: once `beforeSignIn` passes it sends the tab
 * to the provider. The `state` (a random nonce, plus where to land when `next` is given)
 * is made and remembered here, not during render: a prerendered page would carry the
 * server's nonce and a press before hydration would fail the callback's state check.
 */
const useWebOAuthLogin = (
  provider: OAuthProvider,
  {
    endpoint,
    clientId,
    redirectPath,
    scope,
    next,
    beforeSignIn,
  }: WebOAuthLoginOptions,
) =>
  useCallback(async () => {
    if (beforeSignIn && !(await beforeSignIn())) return;
    const nonce = PKCE.generateRandom(NONCE_LENGTH);
    const state = next ? withNextInState(nonce, next) : nonce;
    saveOAuthState(provider, state);
    const params = new URLSearchParams({
      redirect_uri: makeRedirectUri({ path: redirectPath }),
      client_id: clientId,
      response_type: "token",
      state,
      scope,
    });
    window.location.assign(`${endpoint}?${params.toString()}`);
  }, [provider, endpoint, clientId, redirectPath, scope, next, beforeSignIn]);

export default useWebOAuthLogin;
